import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Text,
  Flex,
  Spacer,
  Square,
  Icon,
  Tooltip
} from '@chakra-ui/react';
import { processDateSort, processNumberSort } from 'utils/sort';
import { filterListingsByQuery } from 'utils/search';
import { ListingModel } from 'models';
import ListingAccordion from '../ListingAccordion/ListingAccordion';
import SearchBar from '../../SearchBar/SearchBar';
import ListingGridFilters from '../ListingGridFilters/ListingGridFilters';
import SortDropdown from '../../SortDropdown/SortDropdown';
import ListingGridSkeleton from '../ListingGridSkeleton/ListingGridSkeleton';
import { ListingSortFilter } from '../types';
import { AiOutlineFilter, AiFillFilter, AiOutlineTag } from 'react-icons/ai';

export enum ListingGridSelectionType {
  NONE = 'None',
  SINGLE = 'Single',
  MULTI = 'Multi'
}

export interface ListingGridProps {
  gridListings: ListingModel[];
  isLoading: boolean;
  showFilters?: boolean;
  showFiltersLabel?: boolean;
  showAssociatedItems?: boolean;
  showAssociationAction?: boolean;
  onListingClick?: (listing: ListingModel) => void;
  onListingNameClick?: (id: string, name: string) => void;
  onListingItemClick?: (id: string, name: string) => void;
  onListingArtistClick?: (id: string, name: string) => void;
  onItemSuggestionClick?: (listing: ListingModel) => void;
  onTagListingClick?: (listing: ListingModel, action?: 'FAKE' | 'SET' | 'ORIGINAL') => void;
  onRemoveListingClick?: (listingId: string) => void;
  onUnlinkClick?: (listing: ListingModel) => void;
  unlinkLoadingId?: string | null;
  onBulkTagClick?: () => void;
  gridSelection?: ListingGridSelectionType;
  defaultSelectedListings?: ListingModel[];
  onListingCardClick?: (listing: ListingModel) => void;
  compactMode?: boolean;
}

export default function ListingGrid({ gridListings, isLoading, onListingClick, onListingNameClick, onListingItemClick, onListingArtistClick, onItemSuggestionClick, onTagListingClick, onRemoveListingClick, onUnlinkClick, unlinkLoadingId, onBulkTagClick, showFilters = true, showFiltersLabel = true, showAssociatedItems = false, showAssociationAction = false, gridSelection = ListingGridSelectionType.NONE, defaultSelectedListings = [], onListingCardClick = () => {}, compactMode = false }: ListingGridProps) {
  const [listings, setListings] = useState<ListingModel[]>(gridListings);
  const [selectedListings, setSelectedListings] = useState<ListingModel[]>(defaultSelectedListings);
  const [listingsQuery, setListingsQuery] = useState<string>('');
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [selectedPrices, setSelectedPrices] = useState<number[]>([]);
  const [listingsSort, setListingsSort] = useState<ListingSortFilter>(ListingSortFilter.DEFAULT);
  const [sortType, setSortType] = useState<'asc' | 'desc'>('desc');
  const [displayFilters, setDisplayFilters] = useState<boolean>(false);

  const processSortKey = useCallback((listings: ListingModel[], sortKey: ListingSortFilter): ListingModel[] => {
    let sortedListings = [...listings];
    const isAscending: boolean = sortType === 'asc';

    if (sortType === 'asc') {
      if (sortKey === ListingSortFilter.TITLE) {
        sortedListings = sortedListings.sort((a, b) => (a.name > b.name ? 1 : -1));
      }
    } else {
      if (sortKey === ListingSortFilter.TITLE) {
        sortedListings = sortedListings.sort((a, b) => a.name > b.name ? -1 : 1);
      }
    }

    if (sortKey === ListingSortFilter.SALE_DATE) {
      sortedListings = sortedListings.sort((a, b) => processDateSort(a.date, b.date, !isAscending));
    } else if (sortKey === ListingSortFilter.PRICE) {
      sortedListings = sortedListings.sort((a, b) => processNumberSort(a.rawPrice, b.rawPrice, isAscending));
    }
    return sortedListings;
  }, [sortType]);

  const applyFiltersAndSort = useCallback(() => {
    let filteredListings = gridListings;

    if (listingsQuery) {
      filteredListings = filterListingsByQuery(filteredListings, listingsQuery);
    }

    if (selectedYears.length > 0) {
      filteredListings = filterListingsBySaleDateRange(filteredListings, selectedYears);
    }

    if (selectedPrices.length > 0) {
      filteredListings = filterListingsBySalePriceRange(filteredListings, selectedPrices);
    }

    setListings(processSortKey(filteredListings, listingsSort));
  }, [gridListings, listingsQuery, selectedYears, selectedPrices, listingsSort, processSortKey]);

  useEffect(() => {
    if (gridListings.length) {
      setListings(gridListings);
    }

    if (isLoading) {
      onClearFilters();
      resetSort();
      clearQuery();
    }
  }, [gridListings, isLoading]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [applyFiltersAndSort]);

  const isOnFilterMode = (): boolean => {
    return listingsQuery.length > 0 || listingsSort !== ListingSortFilter.DEFAULT || hasFiltersApplied();
  }

  const resetSort = () => {
    setListingsSort(ListingSortFilter.DEFAULT);
  }

  const clearQuery = () => {
    setListingsQuery('');
  }

  const onClearFilters = () => {
    setSelectedYears([]);
    setSelectedPrices([])
    setDisplayFilters(false);
  }

  const hasFiltersApplied = (): boolean => {
    return selectedYears.length > 0 || selectedPrices.length > 0;
  }

  const toggleFilterDisplay = (): void => {
    setDisplayFilters(!displayFilters);
  }

  const onGridListingCardClick = (listing: ListingModel) => {
    if (gridSelection === ListingGridSelectionType.SINGLE) {
      setSelectedListings([listing]);
    } else if (gridSelection === ListingGridSelectionType.MULTI) {
      if (selectedListings.some(selectedListing => selectedListing.id === listing.id)) {
        setSelectedListings(selectedListings.filter(selectedListing => selectedListing.id !== listing.id));
      } else {
        setSelectedListings([...selectedListings, listing]);
      }
    }
    onListingCardClick(listing);
  }

  const onListingSalePriceRangeSelect = (minPrice: number, maxPrice: number): void => {
    const priceRange = [minPrice, maxPrice];
    setSelectedPrices(priceRange);
  }

  const onListingSaleDateRangeSelect = (minYear: number, maxYear: number): void => {
    const yearRange = [minYear, maxYear];
    setSelectedYears(yearRange);
  }

  const onListingSearch = (query: string): void => {
    setListingsQuery(query);
  }

  const filterListingsBySaleDateRange = (listings: ListingModel[], dateYearRanges: number[]): ListingModel[] => {
    return listings.filter((listing: ListingModel) => listing.getDateYear() >= dateYearRanges[0] && listing.getDateYear() <= dateYearRanges[1]);
  }

  const filterListingsBySalePriceRange = (listings: ListingModel[], priceRanges: number[]): ListingModel[] => {
    return listings.filter((listing: ListingModel) => listing.rawPrice >= priceRanges[0] && listing.rawPrice <= priceRanges[1]);
  }

  const getListingData = (): ListingModel[] => {
    if (isOnFilterMode()) {
      return listings;
    } else {
      return gridListings;
    }
  }

  function renderFiltersSort() {
    return (
      <SortDropdown<ListingSortFilter>
        options={ListingSortFilter}
        selectedSortBy={listingsSort}
        sortType={sortType}
        onSortBySelect={setListingsSort}
        onSortTypeToggle={() => setSortType(sortType === 'asc' ? 'desc' : 'asc')}
      />
    );
  }

  function renderFilterButton() {
    return (
      <Tooltip label={'Filter Results'}>
        <Square size={10} border={'1px solid'} borderColor={'gray.300'} cursor={'pointer'} borderRadius={'md'} onClick={() => toggleFilterDisplay()}>
          <Icon as={displayFilters ? AiFillFilter : AiOutlineFilter} />
        </Square>
      </Tooltip>
    );
  }

  function renderTagButton() {
    if (!onBulkTagClick) return null;

    return (
      <Tooltip label={'Tag Results'}>
        <Square size={10} border={'1px solid'} borderColor={'gray.300'} cursor={'pointer'} borderRadius={'md'} onClick={onBulkTagClick}>
          <Icon as={AiOutlineTag} />
        </Square>
      </Tooltip>
    );
  }

  function renderListingGridFilters() {
    if (displayFilters) {
      return (
        <ListingGridFilters
          listings={gridListings}
          onListingPriceRangeSelect={onListingSalePriceRangeSelect}
          onListingSaleDateRangeSelect={onListingSaleDateRangeSelect}
          defaultPriceRange={selectedPrices}
          defaultSaleDateRange={selectedYears}
          showClearOption={hasFiltersApplied()}
          onResetFilters={onClearFilters}
          showTypeFilter={false}
        />
      );
    }
  }

  function renderFilters() {
    if (showFilters) {
      return (
        <Flex minWidth='fit-content' alignItems={{ base: 'start', md: 'center' }} gap='2' marginBottom="40px" p={{ base: '20px', md: '8px 0px' }} direction={{ base: 'column', md: 'row' }}>
          <Box>
            {showFiltersLabel && <Text fontSize='xs'>Filters: </Text>}
          </Box>
          <Flex direction={{ base: 'column', md: 'row' }} width="100%" gap={2}>
            <SearchBar onChange={onListingSearch} defaultValue={listingsQuery} />
            <Flex direction='row' gap={2} width={'100%'}>
              {renderFilterButton()}
              {renderTagButton()}
              <Spacer />
              {renderFiltersSort()}
            </Flex>
          </Flex>
        </Flex>
      );
    }
  }

  function renderContent() {
    if (isLoading) {
      return (
        <ListingGridSkeleton rows={gridListings.length || 10} />
      );
    } else {
      return (
        <Flex direction="column" gap="12px">
          {getListingData().map((listing: ListingModel, index: number) => (
            <ListingAccordion
              key={`listing_grid_listing_${index}`}
              listing={listing}
              onClick={gridSelection !== ListingGridSelectionType.NONE ? () => onGridListingCardClick(listing) : (onListingClick ? () => onListingClick(listing) : undefined)}
              onListingClick={onListingNameClick}
              onItemClick={onListingItemClick}
              onArtistClick={onListingArtistClick}
              onItemSuggestionClick={onItemSuggestionClick}
              onTagListingClick={onTagListingClick}
              onRemoveListingClick={onRemoveListingClick}
              onUnlinkClick={onUnlinkClick}
              unlinkLoading={unlinkLoadingId === listing.id}
              showAssociatedItem={showAssociatedItems}
              showAssociationAction={showAssociationAction}
              selected={!!selectedListings.find((selectedListing) => selectedListing.id === listing.id)}
              compactMode={compactMode}
            />
          ))}
        </Flex>
      );
    }
  }

  return (
    <Box width="100%">
      {renderFilters()}
      {renderListingGridFilters()}
      {renderContent()}
    </Box>
  );
}
